import React, { useCallback, useContext, useEffect, useState } from 'react';
import { View } from 'react-native';
import { useMutation } from '@apollo/client';
import { useForm } from 'react-hook-form';
import { DateTime } from 'luxon';

import { AuthContext } from 'app/src/contexts/AuthContext';
import { Button, ButtonGroup } from 'app/src/elements/buttons';
import { Fields, defaultValues } from 'app/src/elements/hookForms';
import NestedFields from 'app/src/elements/NestedFields';
import MutationErrors from 'app/src/elements/MutationErrors';

import { CREATE_TXN } from './queries';
import METHODS from './methods';
import TYPES from './types';

import { getBalance, prepareTxnInput } from './utils';

export const FIELDS = {
  bankDate: {
    label: 'Date',
    type: 'date',
    rules: { required: true },
  },
  type: {
    label: 'Type',
    select: TYPES,
    rules: { required: true },
  },
  method: {
    label: 'Method',
    select: METHODS,
    blank: 'null',
  },
  reference: {
    label: 'Reference #',
  },
  name: {
    type: 'entityName',
    preferredTypes: [],
  },
  memo: {},
};

export const LINE_FIELDS = {
  accountId: {
    label: 'Account',
    type: 'account',
  },
  debitLessCredit: {
    label: 'Debit',
    width: 120,
  },
  creditLessDebit: {
    label: 'Credit',
    width: 120,
  },
  assetId: {
    label: 'Asset',
    type: 'asset',
    extraLabels: {
      saleAssetId: 'Lot/Sale',
      feeId: 'Fee',
    },
  },
  memo: { },
};

export const EMPTY_LINE = {
  accountId: '',
  debitLessCredit: '',
  memo: '',
};

const NewTxn = ({ setShowModal, refetch }) => {
  const { accounts } = useContext(AuthContext);

  const emptyLines = [{...EMPTY_LINE}, {...EMPTY_LINE}];
  const [nestedResources, setNestedResources] = useState(emptyLines);
  const [formStatus, setFormStatus] = useState();
  const [mutationErrors, setMutationErrors] = useState();

  const {
    register, control, handleSubmit, setValue, watch, formState: { errors }
  } = useForm({
    defaultValues: defaultValues({
      fields: FIELDS,
      resource: { bankDate: DateTime.now().toISODate() },
      nestedFields: [{
        nestedResources,
        prefix: 'txnLinesAttributes',
        fields: LINE_FIELDS,
      }],
    })
  });

  const [createTxn] = useMutation(CREATE_TXN, {
    onCompleted: data => {
      if (data.createTxn.errors?.length) {
        setFormStatus(null);
        setMutationErrors(data.createTxn.errors);
      } else {
        refetch();
        setShowModal(false);
      }
    }
  });

  const onSubmit = useCallback(input => {
    const balance = getBalance({
      accounts,
      txnLines: input.txnLinesAttributes,
    });

    if (balance.abs > 0.0001) {
      setMutationErrors([
        {
          path: ['Transaction lines'],
          message: 'with a posting account must balance to zero ' +
            `(current balance: ${balance})`
        }
      ]);
    } else {
      setFormStatus('submitting');
      createTxn({ variables: prepareTxnInput(input, FIELDS) });
    }
  });

  const watchAll = watch();

  useEffect(() => {
    const txnLinesWatch = watchAll?.txnLinesAttributes;

    if (txnLinesWatch) {
      const lastLine = txnLinesWatch[txnLinesWatch.length - 1];

      if (lastLine.debitLessCredit) {
        const prefix = `txnLinesAttributes.${nestedResources.length}`;

        register(`${prefix}.accountId`, { value: '' });
        register(`${prefix}.debitLessCredit`, { value: '' });
        register(`${prefix}.memo`, { value: '' });
        setNestedResources([...nestedResources, EMPTY_LINE]);
      }
    }
  }, [watchAll]);

  const lineFields = { ...LINE_FIELDS };

  return (
    <View>
      <Fields
        fields={FIELDS}
        control={control}
        errors={errors}
        setValue={setValue}
      />

      <NestedFields
        prefix="txnLinesAttributes"
        resources={nestedResources}
        fields={lineFields}
        control={control}
        errors={errors}
        setValue={setValue}
      />

      <MutationErrors errors={mutationErrors} />

      <ButtonGroup>
        <Button label="Create Transaction" onPress={handleSubmit(onSubmit)} />
      </ButtonGroup>
    </View>
  );
};

export default NewTxn;
